import { ChooseStoreCard } from '../choose-store-card/choose-store-card'
import {
  Secondary,
  TextField,
  Text,
  TextLink,
  SvgIcon,
} from '@cinch-labs/design-system'
import { useState, useEffect, useCallback, useRef } from 'react'
import { round } from 'lodash'
import styles from './find-a-store.module.css'
import { useFetchAllStoresAndCalculateDistance } from '../../hooks/fetch-stores-with-distance'
import { LoadingSpinner } from '@cinch-nx/shared-ui'
import { useMaintenanceContext } from '../../context/maintenance-context'
import { CarMaintenanceStepWrapper } from '../car-maintenance-step-wrapper/car-maintenance-step-wrapper'
import {
  EventNames,
  TrackingEventTypes,
  useAnalytics,
} from '@cinch-nx/shared-analytics'
import { datadogRum } from '@datadog/browser-rum'
import { useMarshallExplanationDrawer } from './marshall-explanation-drawer'
import { StoreMap } from './find-a-store-map'
import { StoreWithDistance } from '../../hooks/types'
import useIsMobile from '../../hooks/use-is-mobile'
import cx from 'classnames'
import { MobileMapOverlay } from '../mobile-map-overlay/mobile-map-overlay'

const METERS_TO_MILES_CONVERSION = 0.000_621_371
const UK_POSTCODE_REGEX = /^([A-Z]{1,2}\d[A-Z\d]? ?\d[A-Z]{2})$/i

const toMiles = (meters: number) =>
  round(meters * METERS_TO_MILES_CONVERSION, 1)

export const FindAStore: React.FC = () => {
  const [postCode, setPostCode] = useState('')
  const { trackEvent } = useAnalytics()
  const [shouldFetch, setShouldFetch] = useState(false)
  const [visibleCount, setVisibleCount] = useState(0)
  const [inputError, setInputError] = useState('')
  const [selectedStoreOnMap, setSelectedStoreOnMap] =
    useState<StoreWithDistance>()
  const [mapHidden, setMapHidden] = useState(false)
  const [showMobileMapOverlay, setShowMobileMapOverlay] = useState(false)
  const [mobileMapSelectedStore, setMobileMapSelectedStore] =
    useState<StoreWithDistance>()
  const userInteracted = useRef(false)
  const { isMobile } = useIsMobile()

  const { storesWithDistance, customerCoordinates, loading, error } =
    useFetchAllStoresAndCalculateDistance(shouldFetch ? postCode : undefined)

  const { setShowMarshallExplanationDrawer } = useMarshallExplanationDrawer()

  const { dispatch, postcode: ctxPostCode, step } = useMaintenanceContext()

  useEffect(() => {
    if (error === 'Postcode') {
      setInputError('Please enter a valid postcode (e.g. RG27 9XA).')
    } else if (error === 'Stores') {
      setInputError(
        'An error occurred while finding service centres. Please check your postcode and try again.',
      )
    } else {
      setInputError('')
    }
  }, [error])

  useEffect(() => {
    if (ctxPostCode && !postCode && !userInteracted.current) {
      const trimmed = ctxPostCode.trim()
      if (UK_POSTCODE_REGEX.test(trimmed)) {
        setPostCode(trimmed)
        setShouldFetch(true)
      }
    }
  }, [ctxPostCode, postCode])

  const visibleStores = storesWithDistance.slice(0, visibleCount)
  const remaining = storesWithDistance.length - visibleCount

  const handleChange = useCallback((value: string) => {
    userInteracted.current = true
    setInputError('')
    setShouldFetch(false)
    setPostCode(value)
  }, [])

  const onSubmit = () => {
    const searchStoreEvent = {
      eventName: `Search Stores`,
      type: TrackingEventTypes.ADOBE,
      data: {
        event: {
          name: EventNames.click,
          category: 'cinch_servicing',
          action: 'search_store',
          label: 'make_a_booking',
        },
      },
    }
    trackEvent(searchStoreEvent)

    const trimmed = postCode.trim()
    if (!UK_POSTCODE_REGEX.test(trimmed)) {
      setInputError('Please enter a valid postcode (e.g. RG27 9XA).')
      return
    }
    setInputError('')
    setShouldFetch(true)
    setVisibleCount(2)
  }

  const onShowStoreOnMapClick = (store: StoreWithDistance) => {
    const searchStoreEvent = {
      eventName: `Show Store On Map`,
      type: TrackingEventTypes.ADOBE,
      data: {
        event: {
          name: EventNames.click,
          category: 'cinch_servicing',
          action: isMobile ? 'open_mobile_map_overlay' : 'show_store_on_map',
          label: store.store.name,
        },
      },
    }
    trackEvent(searchStoreEvent)

    if (isMobile) {
      setMobileMapSelectedStore(store)
      setShowMobileMapOverlay(true)
    } else {
      setSelectedStoreOnMap(store)
      setMapHidden(false)
    }
  }

  const onToggleHideMapClick = () => {
    const searchStoreEvent = {
      eventName: `Toggle Store Map`,
      type: TrackingEventTypes.ADOBE,
      data: {
        event: {
          name: EventNames.click,
          category: 'cinch_servicing',
          action: 'toggle_map',
          label: mapHidden ? 'show_map' : 'hide_map',
        },
      },
    }
    trackEvent(searchStoreEvent)

    setMapHidden(!mapHidden)
  }

  const showMore = () => {
    setVisibleCount(visibleCount + 5)
    setSelectedStoreOnMap(undefined)
  }

  useEffect(() => {
    if (selectedStoreOnMap) {
      const storeList = window.document.querySelector('[id="stores-list"]')

      storeList?.scrollTo({
        top: 280 * selectedStoreOnMap.index,
        behavior: 'smooth',
      })
    }
  }, [selectedStoreOnMap])

  const selectStore = useCallback(
    (storeWithDistance: StoreWithDistance) => {
      const { store, distance } = storeWithDistance
      const selectStoreEvent = {
        eventName: `Select Store`,
        type: TrackingEventTypes.ADOBE,
        data: {
          event: {
            name: EventNames.click,
            category: 'cinch_servicing',
            action: 'store_selected',
            label: 'make_a_booking',
            type: store.name,
            value: toMiles(distance),
          },
        },
      }
      trackEvent(selectStoreEvent)

      datadogRum.addAction('Cinch Servicing: Store Selected', {
        step,
        page: window.location.pathname,
        storeName: store.name,
        storeId: store.id,
        postcode: postCode.trim(),
      })

      dispatch({ type: 'SET_STORE', name: store.name, id: store.id })
      dispatch({
        type: 'SET_POSTCODE',
        postcode: postCode.trim().toUpperCase(),
      })
      dispatch({
        type: 'SET_CAN_BOOK_APPOINTMENT',
        canBookAppointment: store.canBookAppointment,
      })
      dispatch({
        type: 'SET_DEALER_ID',
        dealerId: store.dealerId,
      })
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    [dispatch, postCode],
  )

  const canShowMap = !isMobile && !!customerCoordinates && !!postCode
  const showingMap = !!canShowMap && !mapHidden

  return (
    <CarMaintenanceStepWrapper
      title="Find a service centre"
      descriptionElement={
        <Text>
          Enter your postcode to find your nearest service centre from over 100{' '}
          <TextLink
            onClick={() => {
              const marshallExplanationEvent = {
                eventName: `Marshall explanation drawer opened`,
                type: TrackingEventTypes.ADOBE,
                data: {
                  event: {
                    name: EventNames.click,
                    category: 'cinch_servicing',
                    action: 'open',
                    label: 'marshall-explanation',
                    type: 'drawer',
                  },
                },
              }
              trackEvent(marshallExplanationEvent)
              setShowMarshallExplanationDrawer(true)
            }}
          >
            Marshall
          </TextLink>{' '}
          stores.
        </Text>
      }
      showContinueButton={false}
    >
      <div className={styles.container} data-testid="find-store">
        <div className={styles.formContainer}>
          <div className={styles.formItem}>
            <TextField
              id="postcode_input"
              name="Postcode"
              value={postCode}
              placeholder="E.g. RG27 9XA"
              type="text"
              label="Postcode"
              aria-label="Postcode"
              onChange={(e) => handleChange(e.target.value)}
              fullWidth
              error={{
                show: !!inputError,
                message: inputError,
                collapse: true,
              }}
              classNames={{
                container: styles.formTextField,
                input: styles.formTextFieldInput,
              }}
            />
          </div>
          <Secondary
            onClick={onSubmit}
            aria-label="Find a service centre"
            size="large"
            classNames={{ button: styles.formButton }}
            data-testid="find-store-button"
          >
            Find a service centre
          </Secondary>
        </div>
        {loading && <LoadingSpinner size={50} />}

        <div
          className={cx(
            styles.mainContainer,
            showingMap ? styles.mainContainerWide : '',
          )}
        >
          <div className={showingMap ? styles.mainSubContainerWide : ''}>
            {visibleStores.length > 0 && canShowMap && (
              <div
                className={cx(
                  styles.storeOptionHeader,
                  showingMap ? styles.storeOptionHeaderWide : '',
                )}
              >
                <Text
                  element="h2"
                  fontSize="m"
                  fontSizeFixed="m"
                  fontWeight="bold"
                  id="choose-store-heading"
                >
                  Choose one option
                </Text>
                <TextLink onClick={onToggleHideMapClick}>
                  {mapHidden ? (
                    <Text fontSizeFixed="m">
                      <SvgIcon name="location" /> View on map
                    </Text>
                  ) : (
                    <Text fontSizeFixed="m">
                      <SvgIcon name="cross" /> Close map
                    </Text>
                  )}
                </TextLink>
              </div>
            )}

            {visibleStores.length > 0 && !canShowMap && (
              <div className={styles.storeOptionHeader}>
                <Text
                  element="h2"
                  fontSize="m"
                  fontSizeFixed="m"
                  fontWeight="bold"
                  id="choose-store-heading"
                >
                  Choose one option
                </Text>
                {isMobile && (
                  <TextLink
                    onClick={() => {
                      setMobileMapSelectedStore(storesWithDistance[0])
                      setShowMobileMapOverlay(true)
                      trackEvent({
                        eventName: `View on Map`,
                        type: TrackingEventTypes.ADOBE,
                        data: {
                          event: {
                            name: EventNames.click,
                            category: 'cinch_servicing',
                            action: 'view_on_map_link',
                          },
                        },
                      })
                    }}
                  >
                    <Text fontSizeFixed="m">
                      <SvgIcon name="location" /> View on map
                    </Text>
                  </TextLink>
                )}
              </div>
            )}

            <div className={styles.storesContainer}>
              <div
                data-testid={'stores-list'}
                id={'stores-list'}
                className={showingMap ? styles.storesListContainer : ''}
              >
                {visibleStores.map(({ store, distance, index }, i) => (
                  <ChooseStoreCard
                    index={i}
                    key={`${store.id}-${i}`}
                    distance={toMiles(distance)}
                    name={store.name}
                    address={`${store.addressLine1}, ${store.postcode}`}
                    coordinates={store.coordinates}
                    onSelect={() => selectStore({ store, distance, index })}
                    isSelected={store.id === selectedStoreOnMap?.store.id}
                    onMapClick={() =>
                      onShowStoreOnMapClick({ store, distance, index })
                    }
                    showMapLink={canShowMap}
                    customerPostcode={postCode}
                    ariaLabel={`Choose cinch Store ${store.name}`}
                    openingHours={store.openingHours}
                  />
                ))}
              </div>
              {canShowMap && !mapHidden && (
                <div className={styles.mapContainer}>
                  <StoreMap
                    stores={visibleStores}
                    customerLocation={{
                      latitude: customerCoordinates?.latitude,
                      longitude: customerCoordinates?.longitude,
                      postcode: postCode,
                    }}
                    selectedStore={selectedStoreOnMap?.store}
                    setSelectedStore={setSelectedStoreOnMap}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
        {visibleStores.length > 0 && remaining > 0 && (
          <Secondary
            onClick={showMore}
            aria-label={`Show more service centres`}
            classNames={{ button: styles.showMoreButton }}
            size="large"
          >
            {`Show more service centres`}
          </Secondary>
        )}
      </div>

      {isMobile && showMobileMapOverlay && (
        <MobileMapOverlay
          stores={storesWithDistance}
          selectedStore={mobileMapSelectedStore}
          onClose={() => setShowMobileMapOverlay(false)}
          customerPostcode={postCode}
          onSelectStore={(store) => {
            selectStore(store)
            setShowMobileMapOverlay(false)
          }}
        />
      )}
    </CarMaintenanceStepWrapper>
  )
}
